import { useCallback, useMemo } from 'react';
import { useAiBudgetStatusQuery } from 'graphql-common';
import {
  AiBudgetValue,
  useSharedAiBudget,
} from 'components/AiAssistant/AiBudgetContext';
import { AiCredits } from 'components/AiAssistant/AiCreditsIndicator';

export type UseAiBudgetStatusResult = AiBudgetValue;

// The company's AI budget for the current billing period (BE `Query.
// aiBudgetStatus`). Inside `AiBudgetProvider` — i.e. anywhere in the dashboard
// — this returns the one shared result and issues no query of its own; the
// `enabled` argument is then the provider's business. Standalone, it runs the
// query itself, gated on `enabled` because it requires the
// DOCUMENTATION_MANAGEMENT + AI_ASSISTANCE plan features and errors without
// them.
//
// `credits` and `blockedReason` are independent: no configured cap means there
// is no meter to render (`credits: null`) AND every AI call is refused, while a
// cap with nothing left still renders "0 / 1000". Read `credits` for the meter,
// `blockedReason` for whether an AI control may be used at all — deriving the
// second from the first is wrong in both directions (credits round up, and an
// unconfigured cap reads `enforced: false`).
export default function useAiBudgetStatus(
  enabled: boolean,
): UseAiBudgetStatusResult {
  const shared = useSharedAiBudget();
  const { data, refetch } = useAiBudgetStatusQuery({
    skip: !enabled || !!shared,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const status = data?.data;
  const remaining = status?.remainingCredits ?? null;
  const total = status?.totalCredits ?? null;
  const enforced = status?.enforced ?? false;
  const blockedReason = status?.blockedReason ?? null;
  // Stable identity while the values are unchanged so consumers that key memos
  // on `credits` (e.g. the document form fields) don't recompute every render.
  const credits = useMemo<AiCredits | null>(
    () =>
      enforced && remaining != null ? { remaining, total: total ?? 0 } : null,
    [enforced, remaining, total],
  );

  const refetchBudget = useCallback(() => {
    if (enabled) refetch();
  }, [enabled, refetch]);

  return shared ?? { credits, blockedReason, refetch: refetchBudget };
}
